/**
 * AI-provenance marking for exported images (EU AI Act, Article 50).
 *
 * Two layers, deliberately separate:
 *
 *  1. MACHINE-READABLE metadata (`injectAiXmpIntoPng`) — this is the layer the
 *     statute actually mandates under Art. 50(2). We write IPTC
 *     `DigitalSourceType = trainedAlgorithmicMedia` as an XMP packet. Always
 *     applied; never user-toggleable, because it is invisible and there is no
 *     legitimate reason to publish an unmarked synthetic asset.
 *
 *  2. VISIBLE label (`drawAiWatermark`) — a subtle bottom-right "AI generated"
 *     mark. Not required by the statute itself, but recommended by the
 *     Commission's Code of Practice and the practical way a *deployer* meets
 *     the Art. 50(4) disclosure duty when publishing photoreal synthetic
 *     imagery. Toggleable (workspace default + per-export override).
 *
 * Both are applied at EXPORT time only. The stored master keeps Gemini's own
 * bytes (which carry Google's SynthID watermark) untouched and re-editable.
 *
 * Note: C2PA cryptographic signing is the eventual gold standard, but it needs
 * a real signing certificate and key custody, so it is deliberately out of
 * scope here. XMP is unsigned and therefore strippable — it satisfies the
 * "machine-readable" requirement, not tamper-evidence.
 */

/**
 * Workspace-level default for the VISIBLE watermark, mirrored here so plain
 * library code (`downloadUrlAsFile`) can honour it without needing React
 * context. Kept in sync by WorkspaceContext when the active workspace loads.
 *
 * Defaults to `true` so a download can never silently ship unmarked because
 * the workspace hadn't loaded yet.
 */
let defaultWatermarkEnabled = true;

export function setDefaultWatermarkEnabled(enabled: boolean): void {
  defaultWatermarkEnabled = enabled;
}

export function getDefaultWatermarkEnabled(): boolean {
  return defaultWatermarkEnabled;
}

/** IPTC controlled-vocabulary value for fully AI-generated media. */
const DIGITAL_SOURCE_TYPE_AI =
  "http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia";

export const AI_WATERMARK_TEXT = "AI generated";

// ─────────────────────────────────────────────────────────────────────────────
// Layer 2 — visible watermark
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Draw the subtle AI-disclosure label into the bottom-right of a canvas.
 * Sizing is relative to the image so it looks consistent at every export
 * resolution (1K social crop through 4K hero).
 */
export function drawAiWatermark(
  canvas: HTMLCanvasElement,
  text: string = AI_WATERMARK_TEXT,
): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const { width, height } = canvas;

  // ~1.76% of image height, clamped so it stays legible on small crops and
  // doesn't become a billboard on 4K exports.
  const fontSize = Math.max(9, Math.min(Math.round(height * 0.0176), 38));
  const inset = Math.round(width * 0.025);

  ctx.save();
  ctx.font = `500 ${fontSize}px Inter, system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";

  // Dark shadow keeps the mark readable over light backgrounds without
  // needing an opaque plate behind it.
  ctx.shadowColor = "rgba(0, 0, 0, 0.55)";
  ctx.shadowBlur = Math.max(2, Math.round(fontSize * 0.25));
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = Math.max(1, Math.round(fontSize * 0.06));

  ctx.fillStyle = "rgba(255, 255, 255, 0.5)";
  ctx.fillText(text, width - inset, height - inset);
  ctx.restore();
}

// ─────────────────────────────────────────────────────────────────────────────
// Layer 1 — machine-readable XMP, injected into the PNG container
// ─────────────────────────────────────────────────────────────────────────────

function buildXmpPacket(generatorTool: string): string {
  const now = new Date().toISOString();
  // The XMP spec requires a UTF-8 BOM as the packet's `begin` marker; written
  // as an escape so it isn't an invisible character in the source.
  return `<?xpacket begin="\uFEFF" id="W5M0MpCehiHzreSzNTczkc9d"?>
<x:xmpmeta xmlns:x="adobe:ns:meta/">
 <rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">
  <rdf:Description rdf:about=""
    xmlns:Iptc4xmpExt="http://iptc.org/std/Iptc4xmpExt/2008-02-29/"
    xmlns:xmp="http://ns.adobe.com/xap/1.0/"
    xmlns:dc="http://purl.org/dc/elements/1.1/">
   <Iptc4xmpExt:DigitalSourceType>${DIGITAL_SOURCE_TYPE_AI}</Iptc4xmpExt:DigitalSourceType>
   <xmp:CreatorTool>${escapeXml(generatorTool)}</xmp:CreatorTool>
   <xmp:CreateDate>${now}</xmp:CreateDate>
   <dc:description>Image generated using artificial intelligence.</dc:description>
  </rdf:Description>
 </rdf:RDF>
</x:xmpmeta>
<?xpacket end="w"?>`;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// CRC-32 (PNG chunk checksum). Table built once, lazily.
let crcTable: Uint32Array | null = null;
function getCrcTable(): Uint32Array {
  if (crcTable) return crcTable;
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  crcTable = table;
  return table;
}

function crc32(bytes: Uint8Array): number {
  const table = getCrcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * Build a PNG `iTXt` chunk carrying the XMP packet. The keyword
 * "XML:com.adobe.xmp" is the convention readers look for.
 */
function buildXmpChunk(xmp: string): Uint8Array {
  const keyword = "XML:com.adobe.xmp";
  const encoder = new TextEncoder();

  // iTXt payload: keyword \0 compressionFlag compressionMethod
  //               languageTag \0 translatedKeyword \0 text
  const keywordBytes = encoder.encode(keyword);
  const textBytes = encoder.encode(xmp);
  const payload = new Uint8Array(keywordBytes.length + 5 + textBytes.length);
  let o = 0;
  payload.set(keywordBytes, o); o += keywordBytes.length;
  payload[o++] = 0; // null terminator after keyword
  payload[o++] = 0; // compression flag: uncompressed
  payload[o++] = 0; // compression method
  payload[o++] = 0; // empty language tag + null
  payload[o++] = 0; // empty translated keyword + null
  payload.set(textBytes, o);

  const type = encoder.encode("iTXt");
  const chunk = new Uint8Array(12 + payload.length);
  const view = new DataView(chunk.buffer);

  view.setUint32(0, payload.length);
  chunk.set(type, 4);
  chunk.set(payload, 8);

  const crcInput = new Uint8Array(type.length + payload.length);
  crcInput.set(type, 0);
  crcInput.set(payload, type.length);
  view.setUint32(8 + payload.length, crc32(crcInput));

  return chunk;
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function isPng(bytes: Uint8Array): boolean {
  return PNG_SIGNATURE.every((b, i) => bytes[i] === b);
}

/**
 * Insert the AI-provenance XMP packet into a PNG, immediately after the IHDR
 * chunk (the position spec-compliant readers expect for ancillary metadata).
 *
 * Returns the original bytes unchanged if the input isn't a PNG — callers
 * should treat marking as best-effort rather than failing the download.
 */
export function injectAiXmpIntoPng(
  bytes: Uint8Array,
  generatorTool = "Zitiu",
): Uint8Array {
  if (!isPng(bytes)) return bytes;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // Walk chunks to find the end of IHDR. Layout after the 8-byte signature is
  // [len:4][type:4][data:len][crc:4] repeating.
  let offset = 8;
  let insertAt = -1;
  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(
      bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7],
    );
    const next = offset + 12 + length;
    if (type === "IHDR") {
      insertAt = next;
      break;
    }
    if (next <= offset) break; // malformed; bail rather than loop forever
    offset = next;
  }
  if (insertAt < 0) return bytes;

  const chunk = buildXmpChunk(buildXmpPacket(generatorTool));
  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, insertAt), 0);
  out.set(chunk, insertAt);
  out.set(bytes.subarray(insertAt), insertAt + chunk.length);
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Convenience: canvas → marked PNG blob
// ─────────────────────────────────────────────────────────────────────────────

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(",")[1] || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Export a canvas as a PNG object URL carrying AI-provenance marking.
 * `visibleWatermark` controls layer 2 only — the XMP metadata is always added.
 */
export function canvasToMarkedPngUrl(
  canvas: HTMLCanvasElement,
  visibleWatermark: boolean,
): string {
  if (visibleWatermark) drawAiWatermark(canvas);
  const bytes = injectAiXmpIntoPng(dataUrlToBytes(canvas.toDataURL("image/png")));
  const blob = new Blob([bytes], { type: "image/png" });
  return URL.createObjectURL(blob);
}

/**
 * Load an arbitrary image URL, apply provenance marking, and return an object
 * URL for download. Used for the plain (no-overlay) download path, which would
 * otherwise ship the stored master with no visible label.
 */
export async function markImageUrlForExport(
  url: string,
  visibleWatermark: boolean,
): Promise<string> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("Failed to load image for marking"));
    img.src = url;
  });

  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.drawImage(img, 0, 0);

  return canvasToMarkedPngUrl(canvas, visibleWatermark);
}
